import { useMemo } from 'react';

import { useProfile } from '@mint-lib/profile-helpers';
import { useQuery } from '@mint-lib/query-client';
import { Categories, Tags } from '@mint-sdk/api-tags';

type UseTagsParams = {
  isMint2?: boolean;
  categoryType?: string;
  isPlanning?: boolean;
  assignedTags?: { id: string }[];
  visibleCategoryNames?: string[];
};

export const useTagsPlanning = ({
  isMint2,
  categoryType,
  isPlanning,
  assignedTags,
  visibleCategoryNames,
}: UseTagsParams) => {
  // MINT2.0
  const tags = useQuery({
    ...Tags.listQueryOptions({
      params: {
        categoryType: categoryType,
      },
    }),
    enabled: isMint2,
  });
  const categories = useQuery({
    ...Categories.listQueryOptions({
      params: {},
    }),
    enabled: isMint2,
  });

  const { profile } = useProfile();

  const userTags = useQuery({
    ...Tags.listQueryOptions({
      params: {
        userId: String(profile?.id),
      },
    }),
    enabled: isMint2 && isPlanning && !!profile,
  });

  //MINT
  const tagsOld = useQuery({
    ...Tags.listOldQueryOptions({
      params: {
        type: categoryType,
      },
    }),
    enabled: !isMint2,
  });
  const categoriesOld = useQuery({
    ...Categories.listOldQueryOptions({
      params: {
        type: categoryType,
      },
    }),
    enabled: !isMint2,
  });
  const industries = useQuery({
    ...Tags.industriesListOldQueryOptions({
      params: {},
    }),
    enabled: !isMint2 && categoryType === 'brands',
  });

  const assignedTagIdSet = useMemo(() => {
    return new Set(assignedTags?.map((tag) => tag.id));
  }, [assignedTags]);

  const userTagIdSet = useMemo(() => {
    return new Set(userTags.data?.map((tag) => tag.id));
  }, [userTags.data]);

  const visibleTags = useMemo(() => {
    if (!isPlanning) {
      return tags.data;
    }

    return tags.data?.filter((tag) => {
      return assignedTagIdSet.has(tag.id) || userTagIdSet.has(tag.id);
    });
  }, [tags.data, assignedTagIdSet, userTagIdSet, isPlanning]);

  // TODO: will be removed when BE provides treedata structure
  // Please check that Hierarchies on strategy is not broken after migration to tree data structure
  // Because current implementation rely on rowData changes and currently nodesMap is recreated on every render

  // Construct tree data structure (if - MINT2.0 and else - MINT)
  const nodesMap = new Map();
  let tagsWithCategories;

  if (isMint2) {
    // set first level category label
    const firstLevelCategory =
      Object.values(categories.data || {})
        .flatMap((item) => item.categories)
        .find(
          (category1) =>
            category1.level === 1 && category1.type === categoryType,
        )?.name || 'Uncategorized';
    nodesMap.set('uncategorized', {
      name: firstLevelCategory,
      categoryType: 'Uncategorized',
      level: 0,
      children: [],
    });

    //combine tags with their categories
    visibleTags?.forEach((tag) => {
      const categoryAdd =
        Object.values(categories.data || {})
          .flatMap((item) => item.categories)
          .find((category) => category.id === tag.category) || null;
      const categoryType =
        Object.values(categories.data ?? {}).find((category) =>
          category.categories.some((cat) => cat.id === tag.category),
        )?.name || null;

      nodesMap.set(tag.id, {
        ...tag,
        category: categoryAdd?.name || '',
        categoryType,
        level: categoryAdd?.level || 0,
        children: [],
        isDisabled: isPlanning ? !userTagIdSet.has(tag.id) : false,
      });
    });

    nodesMap.forEach((node) => {
      if (node.parent) {
        const parent = nodesMap.get(node.parent);
        if (parent) {
          parent.children.push(node);
        }
      }
    });

    //provide category label as first child
    nodesMap.forEach(
      (node: { children: any[]; name: string; level: number }) => {
        if (node.children?.length > 0) {
          const categoryName = node.children[0].category;
          node.children = [
            { name: categoryName, id: node.name },
            ...node.children,
          ];
        }
      },
    );
  } else {
    const categoryMap = new Map(categoriesOld.data?.map((c) => [c.id, c.name]));
    const industryMap = new Map(industries.data?.map((i) => [i.id, i.name]));
    // set first level category label
    const firstLevelCategory =
      Object.values(categoriesOld.data || {}).find(
        (category1) => category1.level === 1 && category1.type === categoryType,
      )?.name || '';
    nodesMap.set('uncategorized', {
      name: firstLevelCategory,
      category: 'Uncategorized',
      categoryType: 'Uncategorized',
      level: 0,
      children: [],
    });
    tagsOld.data?.forEach((tag) => {
      return nodesMap.set(tag.id, {
        id: tag.id,
        name: tag.name,
        category: tag.category ? (categoryMap.get(tag.category) ?? null) : null,
        industry: tag.industry ? (industryMap.get(tag.industry) ?? null) : null,
        avatar: tag.avatar,
        users_count: tag.usersCount,
        parent: tag.parent,
        children: [],
      });
    });

    nodesMap.forEach((node) => {
      if (node.parent) {
        const parent = nodesMap.get(node.parent);
        if (parent) {
          parent.children.push(node);
        }
      }
    });

    nodesMap.forEach((node: { children: any[]; name: string }) => {
      if (node.children?.length > 0) {
        const categoryName = node.children[0].category;
        node.children = [{ name: categoryName }, ...node.children];
      }
    });
  }

  const visibleCategoryEntries = useMemo(() => {
    const categoryEntries = Object.entries(categories.data ?? {});

    if (!isPlanning) {
      return categoryEntries;
    }

    return categoryEntries.filter(([_, categoryGroup]) => {
      return visibleCategoryNames?.includes(categoryGroup.name);
    });
  }, [categories.data, isPlanning, visibleCategoryNames]);

  // tabs are used in LabelModal to filter tags by categoryType
  const tabs = visibleCategoryEntries.map(([id, value]) => ({
    id,
    name: value.name,
  }));

  return {
    tags: tagsWithCategories,
    tabs,
    isLoading:
      tags.isLoading ||
      categories.isLoading ||
      tagsOld.isLoading ||
      categoriesOld.isLoading ||
      industries.isLoading ||
      userTags.isLoading,
    isRefetching: tags.isRefetching || categories.isRefetching,
    refetch: tags.refetch,
    nodesMap: Array.from(nodesMap?.values() ?? []),
  };
};
